'use client'

import AddressFields, {
    AddressFormValue,
    formatAddressLine,
} from '@/components/address/address-fields'
import {
    Address,
    createAddress,
    getAppointmentAddresses,
    updateAddress,
} from '@/lib/api/addresses'
import { formatCEP } from '@/lib/cep'
import { useClientLabel } from '@/lib/hooks/use-client-label'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
    MdAdd,
    MdCheckCircle,
    MdEdit,
    MdInfo,
    MdLocationOn,
    MdWarning,
} from 'react-icons/md'
import { toast } from 'react-toastify'

export type ServiceAddressSource =
    | 'PARTNER'
    | 'PROFESSIONAL'
    | 'HOSPITAL'
    | 'CUSTOM'
    /**
     * O operador declarou que o local ainda NÃO está definido.
     *
     * Não é "campo em branco": nada é gravado e o servidor fica proibido de derivar
     * o endereço do parceiro/profissional. Quem avisa o paciente (lembrete, e-mail,
     * confirmação de cobrança) já sabe dizer que a equipe informará o local.
     */
    | 'UNCONFIRMED'

export interface ServiceAddress {
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
    addressSource?: ServiceAddressSource
}

interface ServiceAddressSectionProps {
    partnerId: number | null
    professionalId: string
    hospitalId?: number | null
    /** Endereço confirmado (null enquanto o operador não confirma). */
    value: ServiceAddress | null
    onChange: (address: ServiceAddress | null) => void
}

const OWNER_LABEL: Record<'partner' | 'professional' | 'hospital', string> = {
    partner: 'Parceiro',
    professional: 'Profissional',
    hospital: 'Hospital',
}

const OWNER_SOURCE: Record<
    'partner' | 'professional' | 'hospital',
    ServiceAddressSource
> = {
    partner: 'PARTNER',
    professional: 'PROFESSIONAL',
    hospital: 'HOSPITAL',
}

type OwnerKey = 'partner' | 'professional' | 'hospital'

export function toServiceAddress(
    address: Address,
    source: ServiceAddressSource,
): ServiceAddress {
    return {
        street: address.street || '',
        number: address.number || '',
        complement: address.complement || '',
        neighborhood: address.neighborhood || '',
        city: address.city || '',
        state: address.state || '',
        zipCode: address.zipCode || '',
        country: address.country || 'BR',
        addressSource: source,
    }
}

/** Escolha explícita de "ainda não definido" — ver `ServiceAddressSource`. */
export function isUnconfirmedAddress(
    address?: ServiceAddress | null,
): boolean {
    return address?.addressSource === 'UNCONFIRMED'
}

function sameAddress(a: ServiceAddress, b: Address): boolean {
    return (
        (a.street || '') === (b.street || '') &&
        (a.number || '') === (b.number || '') &&
        (a.zipCode || '') === (b.zipCode || '') &&
        (a.city || '') === (b.city || '')
    )
}

/**
 * Confirmação do LOCAL DE ATENDIMENTO do agendamento.
 *
 * Lista TODAS as unidades cadastradas do parceiro, do profissional e do hospital
 * envolvidos, para o operador escolher — antes havia um endereço por entidade e
 * uma precedência automática (hospital > parceiro > profissional), então quando
 * o atendimento era em outra unidade só restava editar. E editar aqui gravava por
 * cima do cadastro da entidade: a correção de um agendamento estragava o endereço
 * de todos os outros, e o operador seguinte corrigia de volta.
 *
 * Editar continua existindo e afeta o endereço SELECIONADO — o que agora é seguro,
 * porque cada unidade tem registro próprio: corrigir a "Unidade Centro" não
 * encosta na "Unidade Bairro Sul". O que já foi confirmado em agendamentos
 * anteriores não muda, porque fica congelado no próprio agendamento.
 */
export default function ServiceAddressSection({
    partnerId,
    professionalId,
    hospitalId,
    value,
    onChange,
}: ServiceAddressSectionProps) {
    const [groups, setGroups] = useState<Record<OwnerKey, Address[]>>({
        partner: [],
        professional: [],
        hospital: [],
    })
    const { clientLabel } = useClientLabel()
    const { isHealth } = useServiceLabel()
    // Hospital é conceito da saúde: fora dela o texto cita só parceiro e profissional.
    const ownersText = isHealth
        ? 'parceiro, profissional ou hospital'
        : 'parceiro ou profissional'
    const [loading, setLoading] = useState(false)
    const [editing, setEditing] = useState<Address | null>(null)
    const [draft, setDraft] = useState<AddressFormValue>({})
    const [saving, setSaving] = useState(false)
    const [adding, setAdding] = useState<OwnerKey | null>(null)
    /**
     * Endereço avulso deste atendimento (não vira cadastro de ninguém).
     *
     * É o caminho do PACOTE e de qualquer atendimento sem parceiro/profissional/
     * hospital: não há a quem vincular um `Address`, mas o local existe e o
     * paciente precisa dele. Grava só no snapshot, como `CUSTOM`.
     */
    const [manualOpen, setManualOpen] = useState(false)
    /**
     * Escolha vigente quando o formulário manual foi aberto, para o "Cancelar"
     * devolvê-la. Abrir o formulário DESMARCA o que estava escolhido (senão dois
     * radios ficam marcados ao mesmo tempo), e sem guardar isto o operador que
     * desiste de digitar perde a seleção que já tinha feito.
     */
    const valueBeforeManual = useRef<ServiceAddress | null>(null)

    const professionalIdNumber = professionalId
        ? parseInt(professionalId, 10)
        : undefined

    const load = useCallback(async () => {
        if (!partnerId && !professionalIdNumber && !hospitalId) {
            setGroups({ partner: [], professional: [], hospital: [] })
            return
        }

        setLoading(true)
        try {
            const response = await getAppointmentAddresses({
                partnerId: partnerId || undefined,
                professionalId: professionalIdNumber,
                hospitalId: hospitalId || undefined,
            })
            if (response.success) {
                setGroups(response.result)
            }
        } finally {
            setLoading(false)
        }
    }, [partnerId, professionalIdNumber, hospitalId])

    useEffect(() => {
        load()
    }, [load])

    /**
     * Pré-seleciona o principal quando há uma única origem envolvida.
     *
     * Com mais de uma origem a escolha não é óbvia — escolher por conta própria
     * foi justamente o que fez o sistema errar de unidade até aqui. Nesse caso o
     * operador decide, e o botão de salvar segue bloqueado até ele decidir.
     */
    useEffect(() => {
        // `manualOpen` faz parte da guarda: entrar no modo manual limpa a seleção, e
        // sem isto a pré-seleção remarcaria o endereço do parceiro por baixo do
        // formulário que o operador acabou de abrir para digitar outro.
        if (value || manualOpen) return
        const withAddresses = (
            Object.keys(groups) as OwnerKey[]
        ).filter(key => groups[key].length > 0)
        if (withAddresses.length !== 1) return

        const key = withAddresses[0]
        const list = groups[key]
        if (list.length !== 1) return

        onChange(toServiceAddress(list[0], OWNER_SOURCE[key]))
        // `onChange` vem do pai e muda de identidade a cada render; incluí-lo
        // reexecutaria o efeito em loop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups, value, manualOpen])

    const startEdit = (address: Address) => {
        closeManual(false)
        setEditing(address)
        setAdding(null)
        setDraft({
            label: address.label,
            street: address.street,
            number: address.number,
            complement: address.complement,
            neighborhood: address.neighborhood,
            city: address.city,
            cityCode: address.cityCode,
            state: address.state,
            zipCode: address.zipCode,
            country: address.country,
        })
    }

    const startAdd = (owner: OwnerKey) => {
        closeManual(false)
        setAdding(owner)
        setEditing(null)
        setDraft({ country: 'BR' })
    }

    const cancel = () => {
        setEditing(null)
        setAdding(null)
        setManualOpen(false)
        setDraft({})
    }

    const startManual = () => {
        if (manualOpen) return
        valueBeforeManual.current = value
        setManualOpen(true)
        setEditing(null)
        setAdding(null)
        setDraft(
            value && !isUnconfirmedAddress(value)
                ? {
                      street: value.street,
                      number: value.number,
                      complement: value.complement,
                      neighborhood: value.neighborhood,
                      city: value.city,
                      state: value.state,
                      zipCode: value.zipCode,
                      country: value.country || 'BR',
                  }
                : { country: 'BR' },
        )
        // A escolha anterior sai de cena no ato: digitar um endereço é escolher
        // OUTRO local. Enquanto ele não é confirmado no botão, nada está escolhido —
        // e é por isso que o radio de "Sem endereço confirmado" desmarca aqui.
        if (value) onChange(null)
    }

    const closeManual = (restore: boolean) => {
        if (restore && valueBeforeManual.current) {
            onChange(valueBeforeManual.current)
        }
        valueBeforeManual.current = null
        setManualOpen(false)
        setDraft({})
    }

    /** Usa o endereço digitado SÓ neste atendimento — nada é cadastrado. */
    const useManualAddress = () => {
        if (!draft.street && !draft.city) {
            toast.warning('Informe ao menos o logradouro ou a cidade')
            return
        }
        onChange({
            street: draft.street || '',
            number: draft.number || '',
            complement: draft.complement || '',
            neighborhood: draft.neighborhood || '',
            city: draft.city || '',
            state: draft.state || '',
            zipCode: draft.zipCode || '',
            country: draft.country || 'BR',
            addressSource: 'CUSTOM',
        })
        closeManual(false)
    }

    /** Escolhe um endereço cadastrado — fecha o formulário manual, se estiver aberto. */
    const selectAddress = (address: Address, owner: OwnerKey) => {
        closeManual(false)
        onChange(toServiceAddress(address, OWNER_SOURCE[owner]))
    }

    /** Declara que o local ainda não está definido. */
    const selectUnconfirmed = () => {
        closeManual(false)
        cancel()
        onChange({ addressSource: 'UNCONFIRMED' })
    }

    const save = async () => {
        setSaving(true)
        try {
            if (editing) {
                const response = await updateAddress(editing.id, draft)
                if (!response.success) {
                    toast.error(response.error || 'Erro ao salvar endereço')
                    return
                }
                toast.success('Endereço atualizado')
                // Se o endereço alterado era o confirmado, o agendamento tem de
                // seguir o que está na tela, não a versão antiga.
                if (value && sameAddress(value, editing)) {
                    onChange(
                        toServiceAddress(
                            response.result,
                            value.addressSource || 'PARTNER',
                        ),
                    )
                }
            } else if (adding) {
                const ownerPayload =
                    adding === 'partner'
                        ? { partnerId: partnerId || undefined }
                        : adding === 'professional'
                          ? { professionalId: professionalIdNumber }
                          : { hospitalId: hospitalId || undefined }

                const response = await createAddress({
                    ...ownerPayload,
                    ...draft,
                })
                if (!response.success) {
                    toast.error(response.error || 'Erro ao criar endereço')
                    return
                }
                toast.success('Endereço adicionado')
                onChange(
                    toServiceAddress(response.result, OWNER_SOURCE[adding]),
                )
            }
            cancel()
            await load()
        } finally {
            setSaving(false)
        }
    }

    const availableOwners = (Object.keys(OWNER_LABEL) as OwnerKey[]).filter(
        key =>
            (key === 'partner' && partnerId) ||
            (key === 'professional' && professionalIdNumber) ||
            (key === 'hospital' && hospitalId),
    )

    const totalAddresses = availableOwners.reduce(
        (sum, key) => sum + groups[key].length,
        0,
    )

    const unconfirmed = isUnconfirmedAddress(value)
    const confirmedAddress = value && !unconfirmed ? value : null
    /**
     * A opção "manualmente" está selecionada enquanto o formulário está aberto —
     * e não só depois de confirmado. Sem isto, clicar nela abria o formulário e o
     * radio continuava marcado na opção anterior.
     */
    const manualSelected =
        manualOpen || confirmedAddress?.addressSource === 'CUSTOM'

    return (
        <div className="card bg-base-100 border border-base-300/50 shadow-sm">
            <div className="card-body">
                <h2 className="card-title text-lg flex items-center gap-2">
                    <MdLocationOn className="text-xl" />
                    Local do atendimento
                </h2>
                <p className="text-sm text-base-content/60 mb-2">
                    Escolha onde o {clientLabel} será atendido. É este endereço que vai
                    para a confirmação, os lembretes e os documentos.
                </p>

                {confirmedAddress ? (
                    <div className="alert alert-success py-2 mb-2">
                        <MdCheckCircle className="text-lg" />
                        <span className="text-sm">
                            Confirmado: {formatAddressLine(confirmedAddress)}
                            {confirmedAddress.zipCode
                                ? ` — CEP ${formatCEP(confirmedAddress.zipCode)}`
                                : ''}
                        </span>
                    </div>
                ) : unconfirmed ? (
                    <div className="alert alert-info py-2 mb-2">
                        <MdInfo className="text-lg" />
                        <span className="text-sm">
                            Sem endereço confirmado. Nada é gravado, e o{' '}
                            {clientLabel} é avisado de que a equipe informará o local.
                        </span>
                    </div>
                ) : (
                    <div className="alert alert-warning py-2 mb-2">
                        <MdWarning className="text-lg" />
                        <span className="text-sm">
                            Selecione o local do atendimento para continuar.
                        </span>
                    </div>
                )}

                {loading && (
                    <div className="flex items-center gap-2 text-sm text-base-content/60 py-2">
                        <span className="loading loading-spinner loading-sm" />
                        Carregando endereços...
                    </div>
                )}

                {!loading && totalAddresses === 0 && (
                    <div className="text-sm text-base-content/60 border border-dashed border-base-300 rounded-lg p-4 text-center">
                        {availableOwners.length === 0
                            ? `Este atendimento não tem ${ownersText} de onde tirar um endereço. No pacote, quem executa fica em cada item. Informe o local abaixo ou marque que ele ainda não está definido.`
                            : 'Nenhum endereço cadastrado para as partes deste atendimento. Adicione um abaixo — ele fica salvo no cadastro e passa a aparecer nos próximos agendamentos.'}
                    </div>
                )}

                {!loading &&
                    availableOwners.map(owner => (
                        <div key={owner} className="mt-3">
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-semibold uppercase tracking-wide text-base-content/50">
                                    {OWNER_LABEL[owner]}
                                </span>
                                <button
                                    type="button"
                                    className="btn btn-ghost btn-xs gap-1"
                                    onClick={() => startAdd(owner)}
                                >
                                    <MdAdd size={14} />
                                    Adicionar
                                </button>
                            </div>

                            {groups[owner].length === 0 ? (
                                <p className="text-xs text-base-content/40 pl-1 pb-1">
                                    Sem endereço cadastrado.
                                </p>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {groups[owner].map(address => {
                                        const selected =
                                            !!confirmedAddress &&
                                            confirmedAddress.addressSource ===
                                                OWNER_SOURCE[owner] &&
                                            sameAddress(confirmedAddress, address)
                                        return (
                                            <div
                                                key={address.id}
                                                className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                                                    selected
                                                        ? 'border-secondary bg-secondary/5'
                                                        : 'border-base-300/60 hover:bg-base-200/40'
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    className="radio radio-secondary radio-sm mt-1"
                                                    checked={selected}
                                                    onChange={() =>
                                                        selectAddress(
                                                            address,
                                                            owner,
                                                        )
                                                    }
                                                />
                                                <button
                                                    type="button"
                                                    className="flex-1 min-w-0 text-left"
                                                    onClick={() =>
                                                        selectAddress(
                                                            address,
                                                            owner,
                                                        )
                                                    }
                                                >
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="font-medium text-sm">
                                                            {address.label ||
                                                                'Sem título'}
                                                        </span>
                                                        {address.isPrimary && (
                                                            <span className="badge badge-xs badge-warning">
                                                                Principal
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-base-content/60 mt-0.5">
                                                        {formatAddressLine(
                                                            address,
                                                        ) ||
                                                            'Endereço incompleto'}
                                                    </p>
                                                    {address.zipCode && (
                                                        <p className="text-xs text-base-content/40">
                                                            CEP{' '}
                                                            {formatCEP(
                                                                address.zipCode,
                                                            )}
                                                        </p>
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-ghost btn-xs"
                                                    onClick={() =>
                                                        startEdit(address)
                                                    }
                                                    title="Alterar este endereço"
                                                >
                                                    <MdEdit size={16} />
                                                </button>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    ))}

                {/* Endereço avulso e "ainda não definido": as duas saídas para quando
                    a lista acima não serve — seja porque o local é outro, seja porque
                    não há parceiro/profissional de quem herdar (o caso do pacote). */}
                <div className="mt-4 flex flex-col gap-2 border-t border-base-300/60 pt-3">
                    <div
                        className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                            manualSelected
                                ? 'border-secondary bg-secondary/5'
                                : 'border-base-300/60 hover:bg-base-200/40'
                        }`}
                    >
                        {/* Marcado já na abertura do formulário: é a opção em que o
                            operador está, mesmo antes de terminar de digitar. */}
                        <input
                            type="radio"
                            className="radio radio-secondary radio-sm mt-1"
                            // O rótulo desta opção vive num <button> ao lado (há um
                            // lápis dentro do bloco, e envolver tudo num <label>
                            // faria o clique no lápis marcar o radio), então o nome
                            // acessível vem daqui.
                            aria-label="Informar endereço manualmente"
                            checked={manualSelected}
                            onChange={startManual}
                        />
                        <button
                            type="button"
                            className="flex-1 min-w-0 text-left"
                            onClick={startManual}
                        >
                            <span className="font-medium text-sm">
                                Informar endereço manualmente
                            </span>
                            <p className="text-xs text-base-content/60 mt-0.5">
                                {confirmedAddress?.addressSource === 'CUSTOM'
                                    ? formatAddressLine(confirmedAddress) ||
                                      'Endereço incompleto'
                                    : manualOpen
                                      ? 'Preencha abaixo e clique em "Usar este endereço".'
                                      : 'Vale só para este atendimento — não entra no cadastro de ninguém.'}
                            </p>
                        </button>
                        {confirmedAddress?.addressSource === 'CUSTOM' && (
                            <button
                                type="button"
                                className="btn btn-ghost btn-xs"
                                onClick={startManual}
                                title="Alterar o endereço informado"
                            >
                                <MdEdit size={16} />
                            </button>
                        )}
                    </div>

                    <label
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            unconfirmed
                                ? 'border-secondary bg-secondary/5'
                                : 'border-base-300/60 hover:bg-base-200/40'
                        }`}
                    >
                        <input
                            type="radio"
                            className="radio radio-secondary radio-sm mt-1"
                            checked={unconfirmed}
                            onChange={selectUnconfirmed}
                        />
                        <span className="flex-1 min-w-0">
                            <span className="font-medium text-sm">
                                Sem endereço confirmado ainda
                            </span>
                            <p className="text-xs text-base-content/60 mt-0.5">
                                Nenhum endereço é gravado, e o sistema não usa o do
                                parceiro nem o do profissional no lugar. As mensagens
                                ao {clientLabel} dizem que a equipe informará o local.
                            </p>
                        </span>
                    </label>
                </div>

                {manualOpen && (
                    <div className="mt-4 p-4 rounded-lg border border-secondary/40 bg-base-200/30 flex flex-col gap-4">
                        <div>
                            <h4 className="font-medium text-base-content">
                                Endereço deste atendimento
                            </h4>
                            <p className="text-xs text-base-content/60 mt-1">
                                Fica gravado no atendimento e é o que vai para a
                                confirmação, os lembretes e os documentos. Não altera
                                o cadastro do parceiro{isHealth ? ',' : ' nem'} do
                                profissional{isHealth ? ' nem do hospital' : ''}.
                            </p>
                        </div>

                        <AddressFields
                            value={draft}
                            onChange={setDraft}
                            idPrefix="service-address-manual"
                        />

                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={() => closeManual(true)}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={useManualAddress}
                            >
                                Usar este endereço
                            </button>
                        </div>
                    </div>
                )}

                {(editing || adding) && (
                    <div className="mt-4 p-4 rounded-lg border border-secondary/40 bg-base-200/30 flex flex-col gap-4">
                        <div>
                            <h4 className="font-medium text-base-content">
                                {editing ? 'Alterar endereço' : 'Novo endereço'}
                            </h4>
                            {editing && (
                                <p className="text-xs text-base-content/60 mt-1">
                                    A alteração vale para os próximos
                                    atendimentos nesta unidade. Agendamentos já
                                    confirmados mantêm o endereço informado ao{' '}
                                    {clientLabel}.
                                </p>
                            )}
                        </div>

                        <AddressFields
                            value={draft}
                            onChange={setDraft}
                            showLabel
                            disabled={saving}
                            idPrefix="service-address"
                        />

                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={cancel}
                                disabled={saving}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={save}
                                disabled={saving}
                            >
                                {saving ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
